import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Upload, FileCheck, X } from 'lucide-react';

type KYCDocument = {
  id: string;
  doc_type: string;
  file_url: string;
  status: string;
  admin_note: string | null;
};

export default function KYCUpload({ userId, currentStatus }: { userId: string; currentStatus: string }) {
  const { t } = useLanguage();
  const [documents, setDocuments] = useState<KYCDocument[]>([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDocuments();
  }, [userId]);

  const fetchDocuments = async () => {
    try {
      const { data, error } = await supabase
        .from('kyc_documents')
        .select('*')
        .eq('user_id', userId);

      if (error) throw error;
      setDocuments(data || []);
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (docType: 'id_card' | 'selfie' | 'address_proof', file: File) => {
    if (!file) return;

    setUploading(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${userId}/${docType}-${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('kyc-documents')
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('kyc-documents')
        .getPublicUrl(fileName);

      const { error: dbError } = await supabase
        .from('kyc_documents')
        .insert({
          user_id: userId,
          doc_type: docType,
          file_url: publicUrl,
          status: 'pending',
        });

      if (dbError) throw dbError;

      toast.success('Document uploaded successfully!');
      fetchDocuments();
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setUploading(false);
    }
  };

  const getDocumentStatus = (docType: string) => {
    return documents.find((doc) => doc.doc_type === docType);
  };

  const docTypes: Array<{ type: 'id_card' | 'selfie' | 'address_proof'; label: string }> = [
    { type: 'id_card', label: t('idCard') },
    { type: 'selfie', label: t('selfie') },
    { type: 'address_proof', label: t('addressProof') },
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'bg-green-500';
      case 'rejected':
        return 'bg-red-500';
      case 'pending':
        return 'bg-yellow-500';
      default:
        return 'bg-gray-500';
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          {t('kycStatus')}
          <Badge className={getStatusColor(currentStatus)}>
            {t(currentStatus)}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-center py-4 text-muted-foreground">Loading...</div>
        ) : (
          <div className="space-y-6">
            {docTypes.map(({ type, label }) => {
              const doc = getDocumentStatus(type);
              return (
                <div key={type} className="space-y-2">
                  <Label>{label}</Label>
                  <div className="flex items-center gap-4">
                    {doc ? (
                      <div className="flex items-center gap-2 flex-1">
                        <FileCheck className="h-5 w-5 text-green-600" />
                        <span className="text-sm flex-1">Uploaded</span>
                        <Badge className={getStatusColor(doc.status)}>
                          {t(doc.status)}
                        </Badge>
                      </div>
                    ) : (
                      <div className="flex-1">
                        <Input
                          type="file"
                          accept="image/*,.pdf"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleUpload(type, file);
                          }}
                          disabled={uploading}
                        />
                      </div>
                    )}
                  </div>
                  {doc?.admin_note && (
                    <p className="text-sm text-muted-foreground italic">{doc.admin_note}</p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}